import React, {useCallback, useEffect, useMemo, useState} from "react";
import {
    Accordion, AccordionDetails, AccordionSummary,
    Pagination, Paper, Stack,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Typography
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {ObservationReportPaginatedResponse} from "../data/ObservationReportPaginatedResponse";
import {ObservationReportStatsResponse} from "../data/ObservationReportStatsResponse";
import {Division} from "../data/Division";
import {RankResponse} from "../data/RankResponse";
import {LoadingContent} from "../components/LoadingContent";
import {formatTimestamp, formatDate} from "../utils";
import {PoliceUser} from "../components/PoliceUser";
import {ObservationReportViewForm} from "../components/profile/ObservationReportViewForm";
import {
    ObservationFilterPanel,
    ObservationFilterState,
    emptyObservationFilters,
    buildObservationSearchUrl,
    buildObservationStatsUrl,
    isObservationFiltering
} from "../components/observation/ObservationFilterPanel";
import {ObservationStatsPanel} from "../components/observation/ObservationStatsPanel";
import useApiCall from "../hooks/CancellableApiCall";
import useUser from "../hooks/useUser";

function exportToCsv(
    supervisorCounts: {communityId: string, nick: string, reportCount: number}[],
    minimum: number
) {
    const header = ["Supervisor", "Community ID", "Reports Filed"]
    if (minimum > 0) header.push("Met Minimum")

    const rows = supervisorCounts.map(s => {
        const row: (string | number)[] = [s.nick, s.communityId, s.reportCount]
        if (minimum > 0) row.push(s.reportCount >= minimum ? "Yes" : "No")
        return row
    })

    const csv = [header, ...rows]
        .map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(","))
        .join("\n")

    const blob = new Blob([csv], {type: "text/csv;charset=utf-8;"})
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    const now = new Date()
    const timestamp = now.toISOString().replace(/[:.]/g, "-").slice(0, 19)
    a.download = `supervisor-report-counts-${timestamp}.csv`
    a.click()
    URL.revokeObjectURL(url)
}

export const ObservationReportListingPage = () => {
    const [page, setPage] = useState(1)
    const [filters, setFilters] = useState<ObservationFilterState>(emptyObservationFilters)
    const [minimum, setMinimum] = useState(0)
    const [prioritiseNotMet, setPrioritiseNotMet] = useState(false)
    const [overviewExpanded, setOverviewExpanded] = useState(true)
    const user = useUser()
    const canViewStats = user.hasPermissions("observationReport:statistics")

    const divisionsData = useApiCall<Division[]>({url: "/api/division/list-divisions"})
    const ranksData = useApiCall<RankResponse[]>({url: "/api/rank/list"})

    const searching = isObservationFiltering(filters)
    const searchUrl = useMemo(() => buildObservationSearchUrl(filters, page), [filters, page])
    const statsUrl = useMemo(() => buildObservationStatsUrl(filters), [filters])

    const browseData = useApiCall<ObservationReportPaginatedResponse>({
        url: searching ? undefined : `/api/observation-report/list/${page}/-`,
        deferLoading: searching
    })
    const searchData = useApiCall<ObservationReportPaginatedResponse>({
        url: searching ? searchUrl : undefined,
        deferLoading: !searching
    })
    const statsData = useApiCall<ObservationReportStatsResponse>({url: canViewStats ? statsUrl : undefined, deferLoading: !canViewStats})

    const reportData = searching ? searchData.data : browseData.data
    const isLoading = searching ? searchData.isLoading : browseData.isLoading
    const totalPages = Math.max(1, reportData?.pages ?? 1)

    const filteredStats = statsData.data

    useEffect(() => { setPage(1) }, [filters])

    const handleChangePage = (_: React.ChangeEvent<unknown>, value: number) => setPage(value)

    const handleExport = useCallback(() => {
        exportToCsv(
            filteredStats?.supervisorReportCounts ?? [],
            minimum
        )
    }, [filteredStats, minimum])

    // Summary line for the accordion header when collapsed
    const overviewSummaryLine = useMemo(() => {
        const s = filteredStats
        if (!s) return "Loading..."
        const parts = [`${s.totalReports} reports`, `${s.uniqueOfficers} officers`, `${s.uniqueSupervisors} supervisors`]
        if (minimum > 0) {
            const notMet = s.supervisorReportCounts.filter(o => o.reportCount < minimum).length
            if (notMet > 0) parts.push(`${notMet} supervisors below minimum`)
        }
        return parts.join(" · ")
    }, [filteredStats, minimum])

    return <Stack spacing={2}>

        {/* ── Toolbar ── */}
        <Paper variant="outlined" sx={{p: 2}}>
            <ObservationFilterPanel
                filters={filters}
                divisions={divisionsData.data ?? []}
                ranks={ranksData.data ?? []}
                onChange={f => setFilters(f)}
                onClear={() => setFilters(emptyObservationFilters)}
            />
        </Paper>

        {/* ── Command overview accordion ── */}
        {canViewStats && <Accordion
            expanded={overviewExpanded}
            onChange={(_, v) => setOverviewExpanded(v)}
            variant="outlined"
            disableGutters
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                <Stack direction="row" spacing={2} alignItems="baseline" sx={{width: "100%", pr: 1}}>
                    <Typography variant="subtitle1" fontWeight={600}>Command Overview</Typography>
                    {!overviewExpanded && (
                        <Typography variant="body2" color="text.secondary" noWrap>
                            {overviewSummaryLine}
                        </Typography>
                    )}
                </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{pt: 1}}>
                <ObservationStatsPanel
                    stats={filteredStats}
                    isLoading={statsData.isLoading}
                    minimum={minimum}
                    onMinimumChange={setMinimum}
                    prioritiseNotMet={prioritiseNotMet}
                    onPrioritiseNotMetChange={setPrioritiseNotMet}
                    onExport={handleExport}
                    exportDisabled={!filteredStats?.supervisorReportCounts?.length}
                />
            </AccordionDetails>
        </Accordion>}

        {/* ── Report table ── */}
        <Paper variant="outlined" sx={{p: 2}}>
            <LoadingContent showLoadingSkeleton isLoading={isLoading}>
                <TableContainer sx={{overflowX: "auto"}}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Patrol Date</TableCell>
                                <TableCell>Officer</TableCell>
                                <TableCell>Supervisor</TableCell>
                                <TableCell>Submitted</TableCell>
                                <TableCell/>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(reportData?.observationReports ?? []).map(report =>
                                <TableRow key={report.id.toString()}>
                                    <TableCell sx={{whiteSpace: "nowrap"}}>{formatDate(report.date)}</TableCell>
                                    <TableCell>
                                        <PoliceUser user={{communityId: report.employeeCommunityId, nick: report.employeeNick}}/>
                                    </TableCell>
                                    <TableCell>
                                        <PoliceUser user={{communityId: report.issuerCommunityId, nick: report.issuerNick}}/>
                                    </TableCell>
                                    <TableCell sx={{whiteSpace: "nowrap"}}>{formatTimestamp(report.submittedTimestamp)}</TableCell>
                                    <TableCell>
                                        <ObservationReportViewForm id={report.id}/>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </LoadingContent>
            <Stack alignItems="center" sx={{mt: 2}}>
                <Pagination color="secondary" count={totalPages} page={page} onChange={handleChangePage}/>
            </Stack>
        </Paper>
    </Stack>
}
